<template>
    <div class="card card-body shadow">
        <!-- Steps -->
        <step-indicator v-bind:step="state.step"></step-indicator>

        <!-- HEADER -->
        <div class="row mb-3 ml-0 animated slideInRight">
            <div class="col">
                    <h2 class="">{{stepTitle}}</h2>
                    <p class="">{{stepSubtitle}}</p>
            </div>
        </div>

        <!-- Messages -->
        <messages-output v-bind:messages="state.messages"></messages-output>

        <!-- First Step - Review -->
        <step-review v-if="state.step == 0" v-bind:state="state" @goToNextStep="goToNextStep"></step-review>

        <!-- Second Step - Items -->
        <step-items v-if="state.step == 1" v-bind:state="state" @goToNextStep="goToNextStep"></step-items>

        <!-- Third Step - Schedule -->
        <step-schedule v-if="state.step == 2" v-bind:state="state" @goToNextStep="goToNextStep"></step-schedule>

        <!-- Fourth Step - Print -->
        <step-print v-if="state.step == 3" v-bind:state="state" @goToNextStep="goToNextStep"></step-print>

        <!-- Fifth Step - Complete -->
        <step-complete v-if="state.step == 4" v-bind:state="state" @goToNextStep="goToNextStep"></step-complete>
    </div>
</template>

<script>
    import Messages from '@/helpers/Messages'
    import MessagesOutput from '@/components/MessagesOutput'
    import StepIndicator from '@/components/StepIndicator'
    import StepReview from '@/components/Step/StepReview'
    import StepItems from '@/components/Step/StepItems'
    import StepSchedule from '@/components/Step/StepSchedule'
    import StepPrint from '@/components/Step/StepPrint'
    import StepComplete from '@/components/Step/StepComplete'

    export default {
        name: 'Book',
        props: ['consignmentReturnCode', 'branding'],
        components: {
            MessagesOutput,
            StepIndicator,
            StepReview,
            StepItems,
            StepSchedule,
            StepPrint,
            StepComplete
        },
        data () {
            return {
                msg: 'Test',
                /*
                * State is an object we'll pass around our views etc.
                * It contains information which may be required accross components (e.g. items? dates? etc?)
                */
                state: {
                    /* Request Data */
                    consignmentData: null, // Will store our data from our request for the consigment info
                    updatedConsignmentData: null, // The data returned when "Updating" the consignment
                    printableConsignmentData: null, // The data returned when getting the consignment printables
                    manifestData: null, // Another variable to store data
                    printableManifestData: null, // The data returned when getting the manifest printable

                    /* Variables */
                    step: 0, // Which step are we on... 0=review, 1=items, 2=schedule, 3=print
                    consignmentReturnCode: null, // Set in created, store our consignment number
                    messages: new Messages(), // Our applications messages / feedback
                    branding: this.branding, // Array to hold our errors
                    consignmentIsManifested: false, // If the consignment we load is already manifested
                    isDangerousGoods: false,

                    /* Items Step */
                    items: [], // Contains the items created in the "StepItems" component
                    fieldsForItems: ['qty', 'length', 'width', 'height', 'weight', 'cube', 'ref', 'desc', 'isdg', 'dgcode'], // Use for dynamically creting these fields
                    requiredFieldsForItems: ['qty', 'weight', 'length', 'width', 'height'], // We use this for validation in the "Stepitems" component
                    /* Schedule Step */
                    scheduleDate: null, // The date chosen in "StepSchedule"
                    scheduleTime: { // The time chosen in "StepSchedule"
                        HH: '',
                        mm: ''
                    },
                    scheduleClosingTime: { // Similar to above but for closing
                        HH: '',
                        mm: ''
                    },
                    scheduleTimezone: null,
                    combinedDateTime: null, // This is generated from the result of scheduleDate and scheduleTime (See watch in StepSchedule)
                    combinedClosingDateTime: null, // Similar to above but for the closing time
                    /* Print Step */
                    printManifestNumber: null, // Save the manifest number to use on the print page
                    printableConsignmentNumber: null // Save the consignment number to use on the print page
                }
            }
        },
        created () {
            // Set the default timezone
            this.state.scheduleTimezone = this.$moment.tz.guess()
            this.$moment.tz.setDefault(this.state.scheduleTimezone)
            // Get the route parameters
            this.state.consignmentReturnCode = this.consignmentReturnCode
            // Load our data
            this.loadConsignmentData()
        },
        computed: {
            /**
             * Step Title
             *
             * Returns the current title based upon the current state
             *
             * @return String The title
             */
            stepTitle: function () {
                switch (this.state.step) {
                  case 0:
                    return 'Review'
                  case 1:
                    return 'Items'
                  case 2:
                    return 'Schedule'
                  case 3:
                    return 'Print'
                  case 4:
                    return 'Complete'
                  default:
                    return ''
                }
            },
            /**
             * Step Subtitle
             *
             * Similarly to stepTitle this returns the subtitle
             *
             * @return String The subtitle
             */
            stepSubtitle: function () {
                switch (this.state.step) {
                  case 0:
                    return 'If this information is correct, please continue and add items. If it is not please contact the receiver.'
                  case 1:
                    return 'Please add the items you would like to return below. You must enter all details in for each item.'
                  case 2:
                    return 'Please select a pickup date and time.'
                  case 3:
                    return 'Please print both your label and manifest.'
                  case 4:
                    return 'Done! You have successfully booked a pick up.'
                  default:
                    return ''
                }
            }
        },
        methods: {
            /**
             * Load Consignment Data
             *
             * Makes a request to show the initial consignment data
             */
            loadConsignmentData: function () {
                // On the page load we load the config
                this.$http.post('/', {
                    request: 'GetReturnConsignmentPublic',
                    return: this.state.consignmentReturnCode
                }).then(response => {
                    // Check if there's any errors
                    if (!this.state.messages.check200ResponseForErrors(response.data)) {
                        // Store the response
                        this.state.consignmentData = response.data.consignment
                        // Check if it's been manifested.. I this instance we're going to skip the first steps and just show the result
                        if (typeof this.state.consignmentData.is_manifested !== 'undefined' && this.state.consignmentData.is_manifested === true) {
                            // Mark it as already been manifested
                            this.state.consignmentIsManifested = true
                            // Add a message
                            this.state.messages.addMessage('Pickup has (already) been booked.', 1)
                            // Skip to the end
                            this.goStraightToPrint()
                        } else {
                            // Process the items into a usable format
                            this.processConsignmentItems()
                        }
                    }
                }).catch(e => {
                    this.state.messages.addErrorsFrom400Response(e)
                })
            },
            /**
             * Process Consignment Items
             *
             * Rework consigment data items into a usable format
             */
            processConsignmentItems: function () {
                // That = this
                let that = this
                // only do this if we have items!
                if (typeof this.state.consignmentData.items_qty !== 'undefined') {
                    // For each item
                    for (let i = 0; i < this.state.consignmentData.items_qty.length; i++) {
                        // Check if dangerous goods...
                        if (this.state.consignmentData.items_isdg[i] === true) {
                            this.isDangerousGoods = true
                        }
                        // Setup a variable to populate
                        let newItem = {}
                        // Populate our items array
                        this.state.fieldsForItems.forEach(function (value) {
                            if (typeof that.state.consignmentData['items_' + value] !== 'undefined') {
                                newItem[value] = that.state.consignmentData['items_' + value][0]
                            } else {
                                newItem[value] = null
                            }
                        })
                        this.state.items.push(newItem)
                    }
                }
            },
            /**
             * Go to new step
             *
             * Pushes the state forward
             */
            goToNextStep: function () {
                this.state.step += 1
            },
            /**
             * Go Straight To Print
             *
             */
            goStraightToPrint: function () {
                // Set the state
                this.state.step = 3
            }
        }
    }
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
    .steps
    {
        margin-bottom:1.5rem;
    }
</style>
